/**
 * Material Library picker — opens from a material request item
 * row to prefill the row's name/brand/variety/make/size/unit/rate
 * from a saved library entry.
 *
 * Same shape as the PartyPickerSlideOver — searchable list +
 * category chips + "Create new" CTA that opens the library form
 * on top.
 */
import { useEffect, useState } from 'react';

import { MaterialLibraryFormSlideOver } from '@/components/MaterialLibraryFormSlideOver';
import {
  MATERIAL_CATEGORIES,
  getCategoryConfig,
  type MaterialCategory,
  type MaterialLibraryItem,
} from '@/lib/materialLibrary';
import { useMaterialLibrary } from '@/lib/useMaterialLibrary';

export function MaterialLibraryPickerSlideOver({
  open,
  onClose,
  orgId,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  onPick: (item: MaterialLibraryItem) => void;
}) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<MaterialCategory | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setCreateOpen(false);
      setQuery('');
      setCategory(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, createOpen]);

  const { data, loading } = useMaterialLibrary(orgId, query, category);

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={onClose}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-books" /> Pick from Library
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <i
              className="ph ph-magnifying-glass"
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-tertiary)',
                fontSize: 14,
              }}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              placeholder="Search materials…"
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px 10px 36px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 13,
                background: '#FAFAFA',
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Category chips */}
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              className={`filter-chip${category === null ? ' active' : ''}`}
              onClick={() => setCategory(null)}
            >
              All
            </button>
            {MATERIAL_CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                className={`filter-chip${category === c.key ? ' active' : ''}`}
                onClick={() => setCategory(c.key)}
              >
                <i className={c.icon} /> {c.label}
              </button>
            ))}
          </div>

          {/* Create-new */}
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            style={{
              padding: 12,
              border: '1.5px dashed var(--brand)',
              background: 'var(--brand-bg)',
              color: 'var(--brand)',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              textAlign: 'left',
              fontFamily: 'inherit',
            }}
          >
            <i className="ph-bold ph-plus" /> Create new material
          </button>

          {/* Results */}
          {loading ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: 'var(--text-tertiary)',
                fontSize: 13,
              }}
            >
              Loading library…
            </div>
          ) : data.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: 'var(--text-tertiary)',
                fontSize: 13,
              }}
            >
              {query.trim() || category
                ? 'No matches. Adjust the filter or create a new one.'
                : 'Your library is empty. Use the button above to add the first.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.map((m) => {
                const cfg = getCategoryConfig(m.category);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => onPick(m)}
                    style={{
                      display: 'flex',
                      gap: 10,
                      padding: 10,
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      background: '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: cfg.tintBg,
                        color: cfg.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: 16,
                      }}
                    >
                      <i className={cfg.icon} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {m.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {[m.brand, m.variety, m.make, m.size]
                          .filter(Boolean)
                          .join(' · ') || cfg.label}
                      </div>
                    </div>
                    {typeof m.defaultRate === 'number' && m.defaultRate > 0 ? (
                      <div
                        style={{
                          textAlign: 'right',
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'var(--text-primary)',
                          fontVariantNumeric: 'tabular-nums',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        ₹{m.defaultRate.toLocaleString('en-IN')}
                        <span
                          style={{
                            display: 'block',
                            fontSize: 10,
                            color: 'var(--text-tertiary)',
                            fontWeight: 500,
                          }}
                        >
                          / {m.unit}
                        </span>
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: 10,
                          color: 'var(--text-tertiary)',
                          fontStyle: 'italic',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        / {m.unit}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Nested create form */}
      <MaterialLibraryFormSlideOver
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        orgId={orgId}
        item={null}
      />
    </>
  );
}
